import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { Link } from 'react-router-dom';
import {
  Button, Modal, ModalBody, ModalFooter, ModalHeader,
} from 'reactstrap';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { ResultGrid } from '../../../../../grid/ResultGrid';
import { Column } from '../../../../../grid/Column';
import { DeleteIcon } from '../../../../../icon/DeleteIcon';
import { EditIcon } from '../../../../../icon/EditIcon';
import { ExternalLinkIcon } from '../../../../../table/ExternalLinkIcon';
import { InfoIcon } from '../../../../../icon/InfoIcon';
import {
  bulkEditTsunamis as bulkEditTsunamisAc,
  cancelEdit as cancelEditAc,
  cancelEditConfirmation as cancelEditConfirmationAc,
  openEditConfirmation as openEditConfirmationAc,
  startEdit as startEditAc,
} from '../../../../../../redux/tsunamis/event/data/tsunamiEventDataActions';
import { createInitialForm, formToJson } from '../edit/tsunamiEventEditModel';
import {
  checkboxCell,
  latLonCell,
  textCell,
} from '../../../../../grid/resultGridCells';
import {
  minColumns,
  sort as defaultSort,
  visibleColumns as defaultColumns,
} from '../../../../../../redux/tsunamis/event/data/tsunamiReduxConstants';

const renderEditLink = id => (
  <Link aria-label={`Edit Tsunami Event ${id}`} to={`/view/hazards/tsunami/update-tsunami/${id}`} target="_blank" rel="noopener noreferrer">
    <EditIcon />
  </Link>
);

const renderEarthquakeLink = (id, content) => (
  <Link aria-label={`Earthquake Event ${id}`} to={`/view/hazards/earthquake/event-more-info/${id}`} target="_blank" rel="noopener noreferrer">
    {content}
  </Link>
);

const renderVolcanoLink = id => (
  <Link aria-label={`Volcano Event ${id}`} to={`/view/hazards/volcano/event-more-info/${id}`} target="_blank" rel="noopener noreferrer">
    <ExternalLinkIcon ariaLabel="Link To Volcanic Event" tooltip="View Related Volcanic Event" />
  </Link>
);

const renderInfoLink = id => (
  <Link aria-label={`Tsunami Event ${id}`} to={`/view/hazards/tsunami/event-more-info/${id}`} target="_blank" rel="noopener noreferrer">
    <InfoIcon />
  </Link>
);

const renderRelatedRunupsLink = (id, value) => (
  <Link aria-label={`Runup Event ${id}`} to={`/view/hazards/tsunami/related-runups/${id}`} target="_blank" rel="noopener noreferrer">
    {value}
  </Link>
);

const renderRelatedDepositsLink = (id, value) => (
  <Link aria-label={`Deposit Info ${id}`} to={`/view/hazards/tsunami/related-deposits/${id}`} target="_blank" rel="noopener noreferrer">
    {value}
  </Link>
);

const handleBeforeUnload = (event) => {
  event.preventDefault();
  // eslint-disable-next-line no-param-reassign
  event.returnValue = 'leaving reference';
};

class TsunamiDataGridUnwrapped extends Component {
  constructor(props) {
    super(props);
    this.handleDeleteClick = this.handleDeleteClick.bind(this);
    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.focus = this.focus.bind(this);
    this.handleYesClick = this.handleYesClick.bind(this);
    this.renderModal = this.renderModal.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleEditToggle = this.handleEditToggle.bind(this);
    this.handleToggleColumns = this.handleToggleColumns.bind(this);
    this.deleteCancel = null;
    this.state = {
      modal: false,
      selectedId: null,
    };
  }

  componentDidUpdate(prevProps) {
    const {
      deleting,
      onDeleteCloseModal,
      saving,
      cancelEditConfirmation,
      errors,
      cancelEdit,
    } = this.props;
    if (!deleting && prevProps.deleting) {
      this.closeModal(() => {
        onDeleteCloseModal();
      });
    }
    if (!saving && prevProps.saving) {
      cancelEditConfirmation();
      window.removeEventListener('beforeunload', handleBeforeUnload, true);
      if (!errors.size) {
        cancelEdit();
        onDeleteCloseModal(); // reload
      }
    }
  }


  openModal(selectedId) {
    this.setState({
      modal: true,
      selectedId,
    });
  }

  closeModal(callback) {
    this.setState({
      modal: false,
      selectedId: null,
    }, callback);
  }

  handleDeleteClick(id) {
    this.openModal(id);
  }

  focus() {
    this.deleteCancel.focus();
  }

  handleYesClick() {
    const { deleteTsunami } = this.props;
    const { selectedId } = this.state;
    deleteTsunami({ id: selectedId });
  }

  renderModal() {
    const {
      deleting,
      saving,
    } = this.props;

    const { modal } = this.state;

    if (saving) {
      return (
        <Modal isOpen={modal} onOpened={this.focus} backdrop="static">
          <ModalBody>Saving...</ModalBody>
        </Modal>
      );
    }

    return (
      <Modal isOpen={modal || deleting} onOpened={this.focus} backdrop="static">
        <ModalHeader>Delete Tsunami Event</ModalHeader>
        <ModalBody>
          {deleting ? 'Deleting...' : 'Are you sure you want to delete the tsunami event?'}
        </ModalBody>
        <ModalFooter>
          <Button disabled={deleting} color="danger" onClick={this.handleYesClick}>Delete</Button>
          {' '}
          <Button disabled={deleting} color="secondary" innerRef={(ref) => { this.deleteCancel = ref; }} onClick={() => this.closeModal()}>Cancel</Button>
        </ModalFooter>
      </Modal>
    );
  }

  handleSubmit(data) {
    const { bulkEditTsunamis } = this.props;
    bulkEditTsunamis({ data });
  }

  handleEditToggle(edit) {
    const { startEdit, cancelEdit, tsunamiEvents } = this.props;
    if (edit) {
      const items = {};
      const initialValues = { items };
      tsunamiEvents.forEach((item) => {
        items[`${item.get('id')}`] = createInitialForm(item).toJS();
      });
      startEdit({ initialValues });
      window.addEventListener('beforeunload', handleBeforeUnload, true);
    } else {
      cancelEdit();
      window.removeEventListener('beforeunload', handleBeforeUnload, true);
    }
  }

  handleToggleColumns({ visibleColumns, sort }) {
    const {
      visibleColumns: preVisibleColumns, sort: prevSort, sortData, toggleColumns,
    } = this.props;
    let changed = false;
    if (!visibleColumns.sort().equals(preVisibleColumns.sort())) {
      toggleColumns(visibleColumns);
      changed = true;
    }
    if (!sort.equals(prevSort)) {
      sortData(sort);
      changed = true;
    }
    return changed;
  }

  render() {
    const {
      tsunamiEvents,
      dataManager,
      sort,
      visibleColumns,
      searchParams,
      editConfirmationOpen,
      saving,
      formData,
      cancelEditConfirmation,
      openEditConfirmation,
      initialFormData,
      initialValues,
      editing,
      errors,
    } = this.props;

    return (
      <div className={classnames({ 'haz-data-grid-container': true, 'haz-data-grid-edit': editing })}>

        {this.renderModal()}


        <ResultGrid
          data={tsunamiEvents}
          sort={sort}
          columnChooser
          visibleColumns={visibleColumns}
          handleColumnsToggle={this.handleToggleColumns}
          download
          downloadPrefix="tsunamis"
          downloadSearchParams={searchParams}
          edit={dataManager}
          editing={editing}
          handleEditToggle={this.handleEditToggle}
          initialValues={initialValues}
          handleSubmit={this.handleSubmit}
          editConfirmationOpen={editConfirmationOpen}
          hazardName="Tsunamis"
          saving={saving}
          handleCancel={cancelEditConfirmation}
          formData={formData}
          initialFormData={initialFormData}
          openEditConfirmation={openEditConfirmation}
          formToJson={formToJson}
          defaultColumns={defaultColumns}
          defaultSort={defaultSort}
          minColumns={minColumns}
        >
          {dataManager && (
            <Column alwaysVisible id="del" cell={({ row }) => (<DeleteIcon handleClick={() => this.handleDeleteClick(row.get('id'))} />)} width={30}>
              <DeleteIcon />
            </Column>
          )}
          {dataManager && (
          <Column alwaysVisible id="edit" cell={({ row }) => renderEditLink(row.get('id'))} width={30}>
            <EditIcon />
          </Column>
          )}
          <Column alwaysVisible={dataManager} id="id" width={50} sortable>Id</Column>
          {dataManager && (
          <Column alwaysVisiblesortable id="publish" cell={({ value, row }) => checkboxCell(row, 'publish', value, errors, editing)} width={50} sortable>Publish</Column>
          )}
          <Column id="year" width={45} sortable helpModalId="year" cell={({ value, row }) => textCell(row, 'year', value, errors, editing)}>Year</Column>
          <Column id="month" width={35} sortable helpModalId="month" cell={({ value, row }) => textCell(row, 'month', value, errors, editing)}>Mo</Column>
          <Column id="day" width={35} sortable helpModalId="day" cell={({ value, row }) => textCell(row, 'day', value, errors, editing)}>Dy</Column>
          <Column id="hour" width={35} sortable helpModalId="hour" cell={({ value, row }) => textCell(row, 'hour', value, errors, editing)}>Hr</Column>
          <Column id="minute" width={35} sortable helpModalId="minute" cell={({ value, row }) => textCell(row, 'minute', value, errors, editing)}>Mn</Column>
          <Column id="second" width={35} sortable helpModalId="second" cell={({ value, row }) => textCell(row, 'second', value, errors, editing)}>Sec</Column>
          <Column id="eventValidity" width={85} sortable helpModalId="tsuValidity" cell={({ value, row }) => textCell(row, 'eventValidity', value, errors, editing, { warning: (value === -1) })}>Tsunami Event Validity</Column>
          <Column id="causeCode" width={80} sortable helpModalId="tsuCauseCode" cell={({ value, row }) => textCell(row, 'causeCode', value, errors, editing)}>Tsunami Cause Code</Column>
          <Column id="oceanicTsunami" width={80} sortable helpModalId="tsuOceanicTsunami" cell={({ value, row }) => checkboxCell(row, 'oceanicTsunami', value, errors, editing)}>Oceanic Tsunami</Column>
          <Column
            id="eqMagnitude"
            width={80}
            sortable
            helpModalId="eqMagnitude"
            cell={({ value, row }) => {
              const earthquakeEventId = row.get('earthquakeEventId');
              let content;
              if (earthquakeEventId) {
                if (value != null) {
                  content = renderEarthquakeLink(earthquakeEventId, value);
                } else {
                  content = renderEarthquakeLink(earthquakeEventId, (<ExternalLinkIcon ariaLabel="Link To Earthquake Event" tooltip="View Related Earthquake Event" />));
                }
              }
              return content;
            }}
          >
            Earthquake Magnitude
          </Column>
          <Column id="eqDepth" width={80} sortable helpModalId="eqFocalDepth" cell={({ value, row }) => textCell(row, 'eqDepth', value, errors, editing)}>Focal Depth (km)</Column>
          <Column id="volcanoEventId" width={35} helpModalId="tsuVolcano" cell={({ value }) => value && renderVolcanoLink(value)}>Vol</Column>
          <Column id="moreInfo" width={40} helpModalId="tsuMoreInfo" cell={({ row }) => renderInfoLink(row.get('id'))}>More Info</Column>
          <Column id="numDeposits" width={60} sortable helpModalId="tsuDeposits" cell={({ value, row }) => (value ? renderRelatedDepositsLink(row.get('id'), value) : '')}>Deposits</Column>
          <Column id="country" width={200} sortable helpModalId="tsuCountry" cell={({ value, row }) => textCell(row, 'country', value, errors, editing)}>Country</Column>
          <Column id="area" width={150} sortable helpModalId="tsuArea" cell={({ value, row }) => textCell(row, 'area', value, errors, editing)}>Area</Column>
          <Column id="regionCode" width={50} sortable helpModalId="tsuRegion" cell={({ value, row }) => textCell(row, 'regionCode', value, errors, editing)}>Region</Column>
          <Column id="locationName" width={200} sortable helpModalId="tsuLocationName" cell={({ value, row }) => textCell(row, 'locationName', value, errors, editing)}>Location Name</Column>
          <Column id="latitude" width={100} sortable helpModalId="tsuLatitude" cell={({ value, row }) => latLonCell(row, 'latitude', value, errors, editing)}>Latitude</Column>
          <Column id="longitude" width={100} sortable helpModalId="tsuLongitude" cell={({ value, row }) => latLonCell(row, 'longitude', value, errors, editing)}>Longitude</Column>
          <Column id="maxWaterHeight" width={100} sortable helpModalId="tsuMaxWaterHeight" cell={({ value, row }) => textCell(row, 'maxWaterHeight', value, errors, editing)}>Maximum Water Height (m)</Column>
          <Column
            id="numRunups"
            width={80}
            sortable
            helpModalId="tsuRunups"
            cell={({ value, row }) => (value ? renderRelatedRunupsLink(row.get('id'), value) : value)}
          >
            Number of Runups
          </Column>
          <Column id="tsMtAbe" width={100} sortable helpModalId="tsuMagAbe" cell={({ value, row }) => textCell(row, 'tsMtAbe', value, errors, editing)}>Tsunami Magnitude (Abe)</Column>
          <Column id="tsMtIi" width={100} sortable helpModalId="tsuMagIida" cell={({ value, row }) => textCell(row, 'tsMtIi', value, errors, editing)}>Tsunami Magnitude (Iida)</Column>
          <Column id="tsIntensity" width={70} sortable helpModalId="tsuIntensity" cell={({ value, row }) => textCell(row, 'tsIntensity', value, errors, editing)}>Tsunami Intensity</Column>
          <Column id="warningStatusId" width={50} sortable helpModalId="tsuWarnStatus" cell={({ value, row }) => textCell(row, 'warningStatusId', value, errors, editing)}>Warn Status</Column>
          <Column id="deaths" width={50} sortable helpModalId="tsuDeaths" cell={({ value, row }) => textCell(row, 'deaths', value, errors, editing)}>Deaths</Column>
          <Column id="deathsAmountOrder" width={80} sortable helpModalId="tsuDeathsDescription" cell={({ value, row }) => textCell(row, 'deathsAmountOrder', value, errors, editing)}>Death Description</Column>
          <Column id="missing" width={50} sortable helpModalId="tsuMissing" cell={({ value, row }) => textCell(row, 'missing', value, errors, editing)}>Missing</Column>
          <Column id="missingAmountOrder" width={80} sortable helpModalId="tsuMissingDescription" cell={({ value, row }) => textCell(row, 'missingAmountOrder', value, errors, editing)}>Missing Description</Column>
          <Column id="injuries" width={50} sortable helpModalId="tsuInjuries" cell={({ value, row }) => textCell(row, 'injuries', value, errors, editing)}>Injuries</Column>
          <Column id="injuriesAmountOrder" width={80} sortable helpModalId="tsuInjuriesDescription" cell={({ value, row }) => textCell(row, 'injuriesAmountOrder', value, errors, editing)}>Injuries Description</Column>
          <Column id="damageMillionsDollars" width={60} sortable helpModalId="tsuDamage" cell={({ value, row }) => textCell(row, 'damageMillionsDollars', value, errors, editing)}>Damage ($Mil)</Column>
          <Column id="damageAmountOrder" width={80} sortable helpModalId="tsuDamageDescription" cell={({ value, row }) => textCell(row, 'damageAmountOrder', value, errors, editing)}>Damage Description</Column>
          <Column id="housesDestroyed" width={80} sortable helpModalId="tsuHousesDestroyed" cell={({ value, row }) => textCell(row, 'housesDestroyed', value, errors, editing)}>Houses Destroyed</Column>
          <Column id="housesDestroyedAmountOrder" width={110} sortable helpModalId="tsuHousesDestroyedDescription" cell={({ value, row }) => textCell(row, 'housesDestroyedAmountOrder', value, errors, editing)}>Houses Destroyed Description</Column>
          <Column id="housesDamaged" width={80} sortable helpModalId="tsuHousesDamaged" cell={({ value, row }) => textCell(row, 'housesDamaged', value, errors, editing)}>Houses Damaged</Column>
          <Column id="housesDamagedAmountOrder" width={110} sortable helpModalId="tsuHousesDamagedDescription" cell={({ value, row }) => textCell(row, 'housesDamagedAmountOrder', value, errors, editing)}>Houses Damaged Description</Column>
          <Column id="deathsTotal" width={50} sortable helpModalId="tsuTotalDeaths" cell={({ value, row }) => textCell(row, 'deathsTotal', value, errors, editing)}>Total Deaths</Column>
          <Column id="deathsAmountOrderTotal" width={80} sortable helpModalId="tsuTotalDeathsDescription" cell={({ value, row }) => textCell(row, 'deathsAmountOrderTotal', value, errors, editing)}>Total Death Description</Column>
          <Column id="missingTotal" width={50} sortable helpModalId="tsuTotalMissing" cell={({ value, row }) => textCell(row, 'missingTotal', value, errors, editing)}>Total Missing</Column>
          <Column id="missingAmountOrderTotal" width={80} sortable helpModalId="tsuTotalMissingDescription" cell={({ value, row }) => textCell(row, 'missingAmountOrderTotal', value, errors, editing)}>Total Missing Description</Column>
          <Column id="injuriesTotal" width={50} sortable helpModalId="tsuTotalInjuries" cell={({ value, row }) => textCell(row, 'injuriesTotal', value, errors, editing)}>Total Injuries</Column>
          <Column id="injuriesAmountOrderTotal" width={80} sortable helpModalId="tsuTotalInjuriesDescription" cell={({ value, row }) => textCell(row, 'injuriesAmountOrderTotal', value, errors, editing)}>Total Injuries Description</Column>
          <Column id="damageMillionsDollarsTotal" width={90} sortable helpModalId="tsuTotalDamage" cell={({ value, row }) => textCell(row, 'damageMillionsDollarsTotal', value, errors, editing)}>Total Damage ($Mil)</Column>
          <Column id="damageAmountOrderTotal" width={90} sortable helpModalId="tsuTotalDamageDescription" cell={({ value, row }) => textCell(row, 'damageAmountOrderTotal', value, errors, editing)}>Total Damage Description</Column>
          <Column id="housesDestroyedTotal" width={90} sortable helpModalId="tsuTotalHousesDestroyed" cell={({ value, row }) => textCell(row, 'housesDestroyedTotal', value, errors, editing)}>Total Houses Destroyed</Column>
          <Column id="housesDestroyedAmountOrderTotal" width={130} sortable helpModalId="tsuTotalHousesDestroyedDescription" cell={({ value, row }) => textCell(row, 'housesDestroyedAmountOrderTotal', value, errors, editing)}>Total Houses Destroyed Description</Column>
          <Column id="housesDamagedTotal" width={100} sortable helpModalId="tsuTotalHousesDamaged" cell={({ value, row }) => textCell(row, 'housesDamagedTotal', value, errors, editing)}>Total Houses Damaged</Column>
          <Column id="housesDamagedAmountOrderTotal" width={125} sortable helpModalId="tsuTotalHousesDamagedDescription" cell={({ value, row }) => textCell(row, 'housesDamagedAmountOrderTotal', value, errors, editing)}>Total Houses Damaged Description</Column>
        </ResultGrid>
      </div>
    );
  }
}

TsunamiDataGridUnwrapped.propTypes = {
  tsunamiEvents: ImmutablePropTypes.list.isRequired,
  dataManager: PropTypes.bool.isRequired,
  sortData: PropTypes.func.isRequired,
  sort: ImmutablePropTypes.list.isRequired,
  visibleColumns: ImmutablePropTypes.list.isRequired,
  toggleColumns: PropTypes.func.isRequired,
  searchParams: PropTypes.string.isRequired,
  deleting: PropTypes.bool.isRequired,
  deleteTsunami: PropTypes.func.isRequired,
  onDeleteCloseModal: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  bulkEditTsunamis: PropTypes.func.isRequired,
  editConfirmationOpen: PropTypes.bool.isRequired,
  formData: ImmutablePropTypes.map.isRequired,
  initialFormData: ImmutablePropTypes.map.isRequired,
  openEditConfirmation: PropTypes.func.isRequired,
  cancelEditConfirmation: PropTypes.func.isRequired,
  errors: ImmutablePropTypes.map.isRequired,
  startEdit: PropTypes.func.isRequired,
  cancelEdit: PropTypes.func.isRequired,
  editing: PropTypes.bool.isRequired,
  initialValues: ImmutablePropTypes.map.isRequired,
};


const mapStateToProps = state => ({
  saving: state.getIn(['tsunamiEventDataPage', 'saving']),
  editConfirmationOpen: state.getIn(['tsunamiEventDataPage', 'editConfirmationOpen']),
  formData: state.getIn(['tsunamiEventDataPage', 'formData']),
  initialFormData: state.getIn(['tsunamiEventDataPage', 'initialFormData']),
  errors: state.getIn(['tsunamiEventDataPage', 'errors']),
  editing: state.getIn(['tsunamiEventDataPage', 'editing']),
  initialValues: state.getIn(['tsunamiEventDataPage', 'initialValues']),
});

const mapDispatchToProps = dispatch => bindActionCreators({
  bulkEditTsunamis: bulkEditTsunamisAc,
  openEditConfirmation: openEditConfirmationAc,
  cancelEditConfirmation: cancelEditConfirmationAc,
  startEdit: startEditAc,
  cancelEdit: cancelEditAc,
}, dispatch);

export const TsunamiDataGrid = connect(mapStateToProps, mapDispatchToProps)(TsunamiDataGridUnwrapped);
